import type { ExperimentConfig } from '@/types';

const experiment3Config: ExperimentConfig = {
  id: 'exp3',
  number: 3,
  name: 'Phosphate in Cola',
  description: 'Determine phosphoric acid in cola beverages by the colorimetric method using ammonium vanadomolybdate and spectrophotometry at 400 nm, with a calibration curve of KH₂PO₄ standards.',
  estimatedTime: '120 minutes',
  learningObjectives: [
    'Prepare a 1.0 × 10⁻³ M phosphate stock solution from oven-dried KH₂PO₄ (approx. 0.1360 g in 1 L)',
    'Prepare calibration standards: 2.0×10⁻⁵, 5.0×10⁻⁵, 1.0×10⁻⁴, 5.0×10⁻⁴, 7.5×10⁻⁴ M by dilution in 100 mL volumetric flasks',
    'Understand Beer-Lambert Law: A = abc (absorbance = extinction coefficient × path length × concentration)',
    'Use a spectrophotometer correctly at 400 nm',
    'Decarbonate cola by boiling ~100 mL for 10 minutes covered by a watch glass',
    'Understand the ammonium vanadomolybdate colorimetric method — intensely yellow colour proportional to phosphate',
    'Mix 10 mL phosphate solution with 5 mL ammonium vanadomolybdate solution for each measurement',
    'Dilute cola sample 50-fold with deionised water and add 0.5 mL concentrated H₂SO₄ prior to analysis',
    'Measure absorbance of three separate cola samples and report average phosphoric acid concentration',
  ],
  safetyNotes: [
    'Concentrated H₂SO₄ is used — handle with extreme care, corrosive',
    'Ammonium vanadomolybdate solution — handle with care',
    'Hot surfaces during decarbonation of cola — use caution',
    'Use cuvettes properly — fingerprints affect spectrophotometer readings',
  ],
  requiredEquipment: [
    'Spectrophotometer',
    'Cuvettes',
    '1 L volumetric flask (for stock solution)',
    '100 mL volumetric flasks (6, for standards and sample)',
    '1-, 5-, and 10 mL pipets',
    'Graduated 5 mL pipet',
    'Beaker with watch glass (for decarbonation)',
    'Cola sample',
    'KH₂PO₄ (oven-dried)',
    'Concentrated H₂SO₄',
    'Ammonium vanadomolybdate solution',
    'Deionised water',
  ],
  parts: [
    {
      id: 0,
      name: 'summary',
      title: 'Summary',
      steps: [],
      equipment: [],
      initialState: {},
    },
    {
      id: 1,
      name: 'samplePrep',
      title: 'Part I: Sample Preparation',
      equipment: ['Beaker', 'Watch glass', 'Hot plate'],
      initialState: {
        colaVolume: 0,
        isHeating: false,
        temperature: 25,
        decarbonationProgress: 0,
        isDecarbonated: false
      },
      steps: [
        {
          id: 1,
          title: 'Measure Cola',
          shortDescription: 'Measure about 100 mL of cola into a beaker',
          handler: 'handleMeasureCola',
          infoContent: {
            title: 'Sample Preparation',
            content: 'About **100 mL** of the cola is placed in a beaker covered by a **watch glass** (for the entire class). CO₂ must be absent from the sample prior to analysis because effervescence causes problems in pipetting and volume measurements.',
            whatHappens: 'Cola is measured into a beaker and covered with a watch glass in preparation for decarbonation by boiling.',
            observe: [
              'Measure approximately 100 mL of cola into the beaker',
              'Note the dark colour and visible effervescence of the fresh cola',
              'Cover with a watch glass to prevent splashing during boiling',
            ],
          }
        },
        {
          id: 2,
          title: 'Begin Boiling',
          shortDescription: 'Boil cola for 10 minutes covered by watch glass',
          handler: 'handleStartHeating',
          validation: {
            requires: ['colaVolume'],
          },
          infoContent: {
            title: 'Why Decarbonate?',
            content: `**Boil** the cola for **10 minutes** in a beaker covered by a **watch glass**. Effervescence of CO₂ causes problems in pipetting and other volume measurements, and hence CO₂ must be absent from the sample prior to analysis.

**Observe:** Bubbling will decrease as CO₂ is driven off.`,
            whatHappens: 'Boiling drives dissolved CO2 out of the cola, eliminating effervescence that would interfere with accurate pipetting and volume measurements.',
            observe: [
              'Vigorous bubbling occurs initially as dissolved CO2 escapes',
              'Bubbling gradually decreases over the 10-minute boiling period',
              'Keep the watch glass on to prevent loss of liquid from splashing',
            ],
          }
        },
        {
          id: 3,
          title: 'Monitor Decarbonation',
          shortDescription: 'Wait for bubbling to stop (10 min boiling)',
          handler: 'handleMonitorDecarbonation',
          validation: {
            requires: ['isHeating']
          }
        },
        {
          id: 4,
          title: 'Cool Sample',
          shortDescription: 'Cool to room temperature before transfer',
          handler: 'handleCoolSample',
          validation: {
            requires: ['isDecarbonated']
          }
        },
        {
          id: 5,
          title: 'Prepare Cola for Analysis',
          shortDescription: 'Add 0.5 mL conc. H₂SO₄ and dilute 50-fold with deionised water',
          handler: 'handleDiluteToVolume',
          infoContent: {
            title: 'Cola Sample Preparation',
            content: 'Just prior to the analysis, add **0.5 mL of concentrated sulfuric acid** to the cola sample. Dilute the cooled decarbonated cola sample **50-fold** with deionised water. Three separate samples will be prepared and measured, and the average phosphoric acid concentration reported (accounting for dilution).',
            whatHappens: 'Concentrated sulfuric acid is added to the cooled decarbonated cola, and the sample is diluted 50-fold with deionised water to bring the phosphate concentration within the calibration range.',
            observe: [
              'Handle concentrated H2SO4 with extreme care — it is highly corrosive',
              'The 50-fold dilution significantly lightens the dark cola colour',
              'Prepare three separate diluted samples for triplicate measurement',
            ],
          }
        }
      ]
    },
    {
      id: 2,
      name: 'standards',
      title: 'Part II: Calibration Curve Preparation',
      equipment: ['1 L volumetric flask', '100 mL volumetric flasks (6)', 'Pipettes', 'Ammonium vanadomolybdate solution'],
      initialState: {
        standards: {
          std1: { prepared: false, concentration: '2.0×10⁻⁵ M' },
          std2: { prepared: false, concentration: '5.0×10⁻⁵ M' },
          std3: { prepared: false, concentration: '1.0×10⁻⁴ M' },
          std4: { prepared: false, concentration: '5.0×10⁻⁴ M' },
          std5: { prepared: false, concentration: '7.5×10⁻⁴ M' }
        },
        reagentAdded: false,
        colorDeveloped: false
      },
      steps: [
        {
          id: 1,
          title: 'Prepare Stock Solution',
          shortDescription: 'Weigh ~0.1360 g dry KH₂PO₄, dissolve in 1 L with 0.5 mL conc. H₂SO₄',
          handler: 'handlePrepareStock',
          infoContent: {
            title: 'Stock Solution (1.0 × 10⁻³ M)',
            content: `Weigh approx. **0.1360 g** of dry KH₂PO₄ accurately (record all 4 decimal places) and dissolve in about 500 mL of deionised water using a **1 L volumetric flask**. Add **0.5 mL of concentrated H₂SO₄** and dilute to the mark with water. This gives a stock solution that is **1.0 × 10⁻³ M** in various phosphate species.`,
            whatHappens: 'Oven-dried KH2PO4 is accurately weighed and dissolved in deionised water with a small amount of H2SO4 to prepare a precise 1.0 x 10^-3 M phosphate stock solution.',
            observe: [
              'Record the mass of KH2PO4 to all four decimal places on the analytical balance',
              'The white KH2PO4 powder dissolves readily in deionised water',
              'Dilute to the 1 L mark precisely — the meniscus bottom must touch the mark',
            ],
          }
        },
        {
          id: 2,
          title: 'Prepare 2.0×10⁻⁵ M Standard',
          shortDescription: 'Dilute stock to 2.0×10⁻⁵ M in 100 mL volumetric flask',
          handler: 'handlePrepareStandard',
          infoContent: {
            title: 'Calibration Standards',
            content: `From the 1.0 × 10⁻³ M stock solution, prepare a series of **six solutions** in **100 mL volumetric flasks** with the following phosphate concentrations:

| Standard | Concentration |
|----------|--------------|
| Std 1    | 2.0 × 10⁻⁵ M |
| Std 2    | 5.0 × 10⁻⁵ M |
| Std 3    | 1.0 × 10⁻⁴ M |
| Std 4    | 5.0 × 10⁻⁴ M |
| Std 5    | 7.5 × 10⁻⁴ M |

Label with the precise concentrations.`,
            whatHappens: 'Serial dilutions of the phosphate stock solution are prepared in 100 mL volumetric flasks to create a range of known concentrations for the calibration curve.',
            observe: [
              'Use the correct pipette volume for each dilution to achieve the target concentration',
              'All standards appear colourless at this stage — colour develops after adding vanadomolybdate',
              'Label each flask clearly with its precise concentration',
            ],
          }
        },
        {
          id: 3,
          title: 'Prepare 5.0×10⁻⁵ M Standard',
          shortDescription: 'Dilute stock to 5.0×10⁻⁵ M in 100 mL volumetric flask',
          handler: 'handlePrepareStandard'
        },
        {
          id: 4,
          title: 'Prepare 1.0×10⁻⁴ M Standard',
          shortDescription: 'Dilute stock to 1.0×10⁻⁴ M in 100 mL volumetric flask',
          handler: 'handlePrepareStandard'
        },
        {
          id: 5,
          title: 'Prepare 5.0×10⁻⁴ M Standard',
          shortDescription: 'Dilute stock to 5.0×10⁻⁴ M in 100 mL volumetric flask',
          handler: 'handlePrepareStandard'
        },
        {
          id: 6,
          title: 'Prepare 7.5×10⁻⁴ M Standard',
          shortDescription: 'Dilute stock to 7.5×10⁻⁴ M in 100 mL volumetric flask',
          handler: 'handlePrepareStandard'
        },
        {
          id: 7,
          title: 'Mix Standards with Vanadomolybdate',
          shortDescription: 'Mix 10 mL phosphate solution + 5 mL vanadomolybdate for each standard',
          handler: 'handleAddReagent',
          validation: {
            requires: ['allStandardsPrepared'],
          },
          infoContent: {
            title: 'The Vanadomolybdate Reaction',
            content: `Each point on the calibration curve is obtained by mixing **10 mL** of the phosphate solution with **5 mL** of the ammonium vanadomolybdate solution and measuring the absorbance on the spectrophotometer at **400 nm**.

In dilute phosphate solutions, ammonium metavanadate (NH₄VO₃), ammonium molybdate (NH₄)₂MoO₄ and phosphate condense to form an **intensely yellow coloured compound**. The intensity of the yellow colour is directly proportional to the concentration of phosphate.

**NOTE:** Do all readings at the same time, including the cola sample.`,
            whatHappens: 'Ammonium vanadomolybdate reacts with phosphate to form an intensely yellow heteropoly acid complex whose colour intensity is proportional to phosphate concentration.',
            observe: [
              'Solutions develop a yellow colour after adding vanadomolybdate reagent',
              'Higher phosphate concentrations produce a more intense yellow',
              'The blank (no phosphate) should remain colourless or very faint',
              'Allow sufficient time for full colour development before reading',
            ],
          }
        },
        {
          id: 8,
          title: 'Wait for Color Development',
          shortDescription: 'Allow 10 minutes for full color development',
          handler: 'handleWaitColorDevelopment',
          validation: {
            requires: ['reagentAdded']
          }
        }
      ]
    },
    {
      id: 3,
      name: 'measurement',
      title: 'Part III: Spectrophotometric Measurement',
      equipment: ['Spectrophotometer', 'Cuvettes'],
      initialState: {
        wavelength: 400,
        isZeroed: false,
        measurements: {
          std1: null,
          std2: null,
          std3: null,
          std4: null,
          std5: null,
          colaSample1: null,
          colaSample2: null,
          colaSample3: null
        },
        currentCuvette: null
      },
      steps: [
        {
          id: 1,
          title: 'Set Wavelength',
          shortDescription: 'Set spectrophotometer to 400 nm',
          handler: 'handleSetWavelength',
          infoContent: {
            title: 'Why 400 nm?',
            content: 'The yellow phosphate complex absorbs blue light (around 400 nm) most strongly. We measure at the wavelength of maximum absorption for best sensitivity.',
            whatHappens: 'The spectrophotometer wavelength is set to 400 nm, the absorption maximum of the yellow phosphovanadomolybdate complex, to maximize measurement sensitivity.',
            observe: [
              'Set the wavelength dial precisely to 400 nm',
              'The yellow complex absorbs blue-violet light and transmits yellow',
              'Measuring at the absorption maximum gives the greatest difference between standards',
            ],
          }
        },
        {
          id: 2,
          title: 'Zero Instrument',
          shortDescription: 'Zero the spectrophotometer appropriately',
          handler: 'handleZeroInstrument',
          infoContent: {
            title: 'Zeroing the Spectrophotometer',
            content: 'Zero the spectrophotometer to correct for any absorbance from the solvent and reagents before measuring standards and samples.',
            whatHappens: 'A blank solution (containing vanadomolybdate reagent but no phosphate) is used to set the absorbance to zero, correcting for reagent background.',
            observe: [
              'Insert the blank cuvette and adjust the readout to zero absorbance',
              'Handle cuvettes by the frosted sides only — fingerprints on clear faces affect readings',
              'The blank corrects for any absorbance from the reagent and solvent',
            ],
          }
        },
        {
          id: 3,
          title: 'Measure 2.0×10⁻⁵ M Standard',
          shortDescription: 'Record absorbance of 2.0×10⁻⁵ M standard at 400 nm',
          handler: 'handleMeasureSample',
          validation: {
            requires: ['isZeroed']
          }
        },
        {
          id: 4,
          title: 'Measure 5.0×10⁻⁵ M Standard',
          shortDescription: 'Record absorbance of 5.0×10⁻⁵ M standard at 400 nm',
          handler: 'handleMeasureSample'
        },
        {
          id: 5,
          title: 'Measure 1.0×10⁻⁴ M Standard',
          shortDescription: 'Record absorbance of 1.0×10⁻⁴ M standard at 400 nm',
          handler: 'handleMeasureSample'
        },
        {
          id: 6,
          title: 'Measure 5.0×10⁻⁴ and 7.5×10⁻⁴ M Standards',
          shortDescription: 'Record absorbance of remaining standards at 400 nm',
          handler: 'handleMeasureSample'
        },
        {
          id: 7,
          title: 'Measure Cola Samples (×3)',
          shortDescription: 'Add 5 mL vanadomolybdate to 10 mL dilute cola, measure absorbance of 3 samples',
          handler: 'handleMeasureSample',
          infoContent: {
            title: 'Cola Sample Measurement',
            content: 'Add **5 mL** of the ammonium vanadomolybdate solution to **10 mL** of the dilute cola solution and measure the absorbance at **400 nm**. If the absorbance is not within your calibration range, prepare either a more dilute or more concentrated solution. Measure the absorbance of **three separate samples** and report the average phosphoric acid concentration (remember to account for the dilution).',
            whatHappens: 'The diluted cola reacts with vanadomolybdate to form the yellow complex; three separate samples are measured to obtain a reliable average phosphate concentration.',
            observe: [
              'The cola-vanadomolybdate mixture develops a yellow colour similar to the standards',
              'Check that the absorbance falls within your calibration range; if not, adjust dilution',
              'Measure three separate cola samples and record each absorbance value',
            ],
          }
        }
      ]
    },
    {
      id: 4,
      name: 'analysis',
      title: 'Part IV: Data Analysis',
      equipment: [],
      initialState: {
        calibrationCurve: {
          slope: null,
          intercept: null,
          r2: null
        },
        unknownConcentration: null,
        percentPhosphate: null
      },
      steps: [
        {
          id: 1,
          title: 'Plot Calibration Curve',
          shortDescription: 'Create absorbance vs. concentration plot',
          handler: 'handlePlotCalibration',
          validation: {
            requires: ['allMeasurementsComplete']
          },
          infoContent: {
            title: 'Beer-Lambert Law',
            content: 'The amount of light absorbed is related to the concentration by the Beer-Lambert law: **A = abc**, where A is absorbance, b is solution path length, c is concentration and a is the extinction coefficient. Plot absorbance (ordinate, y) versus concentration of phosphate (abscissa, x). A straight line passing through the origin should be obtained.',
            chemicalEquation: 'A = abc',
            whatHappens: 'Absorbance values are plotted against known phosphate concentrations to create a Beer-Lambert calibration curve, which should yield a straight line through the origin.',
            observe: [
              'Plot absorbance (y-axis) versus phosphate concentration (x-axis)',
              'Points should form a straight line passing through or near the origin',
              'Calculate the line of best fit equation (y = mx + b) and R-squared value',
            ],
          }
        },
        {
          id: 2,
          title: 'Calculate Linear Regression',
          shortDescription: 'Fit line and calculate R-squared',
          handler: 'handleCalculateRegression'
        },
        {
          id: 3,
          title: 'Determine Cola Phosphate Concentration',
          shortDescription: 'Use equation of the line to calculate phosphate concentration',
          handler: 'handleCalculateUnknown',
          infoContent: {
            title: 'Calculating Unknown Concentration',
            content: 'Using the equation of the line from the calibration curve, calculate the concentration of the unknown. Account for any dilutions you may have made (50-fold dilution). Average the three cola sample results.',
            whatHappens: 'The cola sample absorbance is substituted into the calibration equation to determine phosphate concentration, then multiplied by the dilution factor to obtain the original cola concentration.',
            observe: [
              'Substitute each cola absorbance value into the equation of the line to find concentration',
              'Multiply the calculated concentration by 50 to account for the dilution',
              'Average the three results for a more reliable final concentration',
            ],
          }
        },
        {
          id: 4,
          title: 'Report Final Result',
          shortDescription: 'Report average phosphoric acid concentration in cola',
          handler: 'handleCalculateFinalResult',
          infoContent: {
            title: 'Questions',
            content: `**Lab Questions:**
1. Graph the phosphate standard curve (absorbance vs. concentration). Using the equation of the line, calculate the concentration of the unknown. Account for any dilutions. Label axes and provide a descriptive title. (5 marks)

2. You have 18 M H₂SO₄ — you need to make 100 mL of 0.45 M H₂SO₄. How is this best done? Include all calculations. (1 mark)

3. Using calculations determine how you would make 525 mL of a 0.50 M solution of Cl⁻ ions using CaCl₂. (1 mark)`,
            whatHappens: 'The final phosphoric acid concentration is reported and lab questions involving dilution calculations are completed.',
            observe: [
              'Ensure the calibration graph has properly labelled axes and a descriptive title',
              'Account for the 50-fold dilution when reporting the original cola phosphate concentration',
              'Show all work for dilution calculations (M1V1 = M2V2)',
            ],
          }
        }
      ]
    }
  ],
  quiz: {
    experimentId: 'exp3',
    title: 'Phosphate in Cola Quiz',
    questions: [
      {
        id: 'exp3-q1',
        type: 'mcq' as const,
        question: 'What wavelength is used for spectrophotometric measurement in this experiment?',
        options: [
          { id: 'a', text: '510 nm' },
          { id: 'b', text: '400 nm' },
          { id: 'c', text: '550 nm' },
          { id: 'd', text: '650 nm' },
        ],
        correctOptionId: 'b',
        explanation: 'Ammonium vanadomolybdate produces an intensely yellow colour with phosphate, which is measured at 400 nm.',
      },
      {
        id: 'exp3-q2',
        type: 'mcq' as const,
        question: 'Why must the cola be boiled for 10 minutes before analysis?',
        options: [
          { id: 'a', text: 'To sterilize the sample' },
          { id: 'b', text: 'To remove dissolved CO₂ (decarbonation)' },
          { id: 'c', text: 'To concentrate the phosphate' },
          { id: 'd', text: 'To neutralize the acid' },
        ],
        correctOptionId: 'b',
        explanation: 'Cola contains dissolved CO₂ which would interfere with the analysis. Boiling removes the dissolved gas through decarbonation.',
      },
      {
        id: 'exp3-q3',
        type: 'short_answer' as const,
        question: 'State Beer-Lambert Law and explain how it applies to this experiment.',
        sampleAnswer: 'Beer-Lambert Law states A = abc (or A = εbc), where A is absorbance, a (or ε) is the molar absorptivity, b is the path length, and c is the concentration. In this experiment, absorbance of the yellow vanadomolybdate-phosphate complex is proportional to phosphate concentration, allowing us to construct a calibration curve of known standards and determine unknown phosphate concentrations.',
      },
      {
        id: 'exp3-q4',
        type: 'calculation' as const,
        question: 'If the cola sample is diluted 50-fold before analysis, and the measured phosphate concentration from the calibration curve is 2.5 × 10⁻⁴ M, what is the actual phosphate concentration in the original cola (in M)?',
        expectedValue: 0.0125,
        tolerance: 5,
        unit: 'M',
        explanation: 'Multiply by the dilution factor: 2.5 × 10⁻⁴ M × 50 = 0.0125 M',
      },
      {
        id: 'exp3-q5',
        type: 'mcq' as const,
        question: 'What volumes are mixed for each spectrophotometric measurement?',
        options: [
          { id: 'a', text: '5 mL phosphate solution + 10 mL vanadomolybdate' },
          { id: 'b', text: '10 mL phosphate solution + 5 mL vanadomolybdate' },
          { id: 'c', text: '15 mL phosphate solution + 5 mL vanadomolybdate' },
          { id: 'd', text: '10 mL phosphate solution + 10 mL vanadomolybdate' },
        ],
        correctOptionId: 'b',
        explanation: 'For each measurement, 10 mL of phosphate solution is mixed with 5 mL of ammonium vanadomolybdate solution.',
      },
    ],
  },
};

export default experiment3Config;
